import type { Iso } from "./index.js";
import type { Role, DealerRole, AdminTier, AuditAction } from "./access.js";

export const ACT_AS_HEADER = "X-Omni-Act-As";
export const ACT_AS_IDLE_MINUTES = 30;
/** Keys and words from the M1 mock (admin-tiers.html:1912). */
export const ACT_AS_END_REASONS = [
  "stopped",
  "signed_out",
  "idle",
  "replaced",
  "admin_lost_permission",
  "target_disabled",
] as const;
export type ActAsEndReason = (typeof ACT_AS_END_REASONS)[number];
/** Body of every 409 for a session that cannot be used. reason null: missing, or another admin's session (left untouched). */
export interface ActAsEndedBody {
  error: "act_as_ended";
  reason: ActAsEndReason | null;
}
export type ActAsRefusal = "admin" | "self" | "disabled";

/** lens_act_as/{id}. Plan 3.5 fields, id from deps.ids.uuid(). */
export interface ActAsSession {
  id: string;
  adminUid: string;
  adminEmail: string;
  adminName: string;
  adminRole: AdminTier;
  targetEmail: string;
  targetName: string;
  targetRole: DealerRole;
  targetDealerId: string;
  startedAt: Iso;
  lastSeenAt: Iso;
  endedAt: Iso | null;
  endReason: ActAsEndReason | null;
}

export const AUDIT_KINDS = ["act_as.start", "act_as.end", "act_as.request"] as const;
export type AuditKind = (typeof AUDIT_KINDS)[number];

export const AUDIT_ENTITY_KINDS = [
  "quote",
  "order",
  "customer",
  "dealer",
  "user",
  "quote_template",
  "quote_intake",
  "catalog_item",
  "catalog_dismissal",
  "billing_account",
  "grant",
  "sync_run",
  "feedback_request",
  "account_match",
] as const;
export type AuditEntityKind = (typeof AUDIT_ENTITY_KINDS)[number];
export interface AuditEntity {
  kind: AuditEntityKind;
  id: string;
  label: string | null;
}
export interface AuditPerson {
  email: string;
  name: string;
  role: Role;
}

/** lens_audit/{id}. Append-only: the store has append and reads, nothing else. */
export interface AuditEntry {
  id: string;
  at: Iso;
  kind: AuditKind;
  sessionId: string;
  admin: AuditPerson & { uid: string };
  asUser: AuditPerson;
  dealerId: string;
  action: AuditAction;
  method: "POST" | "PUT" | "PATCH" | "DELETE" | null; // null on start and end
  route: string | null; // the Express pattern, e.g. "/api/quotes/:id/send"
  params: Record<string, string>; // req.params only; request bodies are never stored
  status: number | null;
  entity: AuditEntity | null;
  /** Past tense, one line: AUDIT_ACTION_TEXT[action] plus the entity label, e.g. "Sent quote Q-1204". */
  summary: string;
  endReason: ActAsEndReason | null; // act_as.end only
}

/** On Me while acting (index.ts: Me gains `actingAs?: ActingAsView | null`; A6 always sets it). */
export interface ActingAsView {
  sessionId: string;
  admin: { email: string; name: string; role: AdminTier };
  target: { email: string; name: string; role: DealerRole; dealerId: string; dealerName: string };
  startedAt: Iso;
  lastSeenAt: Iso;
  idleMinutes: number;
}

export type ActivityScope = "all_sessions" | "dealer_sessions" | "own_sessions";
/** Exhaustive over Role: admin tiers -> all_sessions; owner -> dealer_sessions; sales, tech -> own_sessions. */
export function activityScopeOf(role: Role): ActivityScope {
  switch (role) {
    case "admin_super":
    case "admin_billing":
    case "admin_sales":
    case "admin_support":
      return "all_sessions";
    case "owner":
      return "dealer_sessions";
    case "sales":
    case "tech":
      return "own_sessions";
    default: {
      const unhandled: never = role;
      throw new Error(`Unhandled role: ${unhandled}`);
    }
  }
}

/** Dealer viewers get { name: "Phoneware", email: null }. */
export interface ActivityActor {
  name: string;
  email: string | null;
}
export interface ActivityChange {
  at: Iso;
  action: AuditAction;
  summary: string;
  entity: AuditEntity | null;
}
export interface ActivitySession {
  id: string;
  admin: ActivityActor;
  target: { email: string; name: string; role: DealerRole };
  dealer: { id: string; name: string };
  startedAt: Iso;
  endedAt: Iso | null;
  endReason: ActAsEndReason | null;
  /** act_as.request entries with a 2xx status, oldest first. */
  changes: ActivityChange[];
}
export interface ActivityView {
  scope: ActivityScope;
  sessions: ActivitySession[];
}
